import axios, { AxiosResponse } from 'axios';
import { Formik, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import { ApiV1Error, isCSRFError, isValidationError } from '../../../../structures/responses/axios';
import routes from '../../../../utils/routes';
import { forgotPasswordValidation } from '../validation';

type ForgotPasswordProps = {
  onCancel?: () => void;
  setIsLoading?: (isLoading: boolean) => void;
  initialEmail?: string;
  className?: string;
};

type ForgotPasswordValues = {
  email: string;
};

const ForgotPassword: React.FC<ForgotPasswordProps> = (props) => {
  const {
    onCancel,
    setIsLoading = (isLoading: boolean) => undefined,
    initialEmail = '',
    className = '',
  } = props;
  const [errorMessage, setErrorMessage] = useState('');
  const [sentFeedback, setSentFeedback] = useState('');

  const { width } = useWindowDimensions();

  // Defines whether the form input should autofocus or not
  const autoFocus = width > 991;

  const initialValues: ForgotPasswordValues = {
    email: initialEmail,
  };

  const onForgotPassword = (
    values: ForgotPasswordValues,
    {
      setErrors,
      setSubmitting,
    }: FormikHelpers<ForgotPasswordValues>,
  ) => {
    setSubmitting(true);
    setIsLoading(true);
    setSentFeedback('');
    setErrorMessage('');

    axios.post(routes.API_FORGOT_PASSWORD_POST, values)
      .then((res: AxiosResponse<{ message: string }>) => {
        setSentFeedback(res.data.message);
      })
      .catch((error: ApiV1Error) => {
        if (isValidationError(error)) {
          setErrors(error.response.data.errors);
        } else if (isCSRFError(error)) {
          setErrorMessage('This form must be refreshed. Please try again.');
        } else {
          setErrorMessage('An unknown error occurred. Please try again.');
        }
      })
      .finally(() => {
        setSubmitting(false);
        setIsLoading(false);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={forgotPasswordValidation}
      onSubmit={onForgotPassword}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {(formikProps) => {
        const {
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        } = formikProps;

        return (
          <div className={`forgot-password ${className}`}>
            <p className="text-muted text-center">
              Enter your email below and we will send you a password reset link.
            </p>

            {errorMessage && (
              <Alert variant="danger">
                {errorMessage}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Form.Group controlId="forgot-email">
                <Form.Control
                  type="email"
                  name="email"
                  aria-label="Email address."
                  placeholder="Email"
                  value={values.email}
                  autoComplete="email"
                  required
                  autoFocus={autoFocus}
                  isInvalid={touched.email && !!errors.email}
                  isValid={!!sentFeedback}
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                />
                <Form.Control.Feedback type="invalid">
                  {touched.email && errors.email}
                </Form.Control.Feedback>
                <Form.Control.Feedback type="valid">
                  {sentFeedback}
                </Form.Control.Feedback>
              </Form.Group>

              {!!onCancel && (
                <Button
                  variant="outline-secondary"
                  type="button"
                  block
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}

              <Button
                variant="primary"
                type="submit"
                block
                disabled={isSubmitting}
              >
                Send Request
                {isSubmitting && (
                  <span className="spinner-border spinner-border-sm my-auto ml-3">
                    <span className="sr-only">Loading...</span>
                  </span>
                )}
              </Button>
            </form>
          </div>
        );
      }}
    </Formik>
  );
};

export default ForgotPassword;
